import { getGroupSchedule, getEndTime } from "../utils/time";
import { getWarnings } from "../utils/validation";

// The alert that fires when someone already in a clinic is dropped into
// another one. Nothing has moved yet at this point: the app used to silently
// treat every such assignment as a transfer, which quietly un-enrolled members
// who were meant to attend both. So the two readings of the action get asked
// about out loud — add a second clinic, or move.

function GroupLine({ group }) {
  const { startTime } = getGroupSchedule(group);
  return (
    <span>
      <span className="font-mono font-bold">{group.id}</span>
      <span className="text-gray-500"> · {group.tier}</span>
      {group.day && startTime && (
        <span className="text-gray-500"> · {group.day} · {startTime}–{getEndTime(group)}{group.court ? ` · ${group.court}` : ""}</span>
      )}
    </span>
  );
}

export default function SecondClinicModal({
  player, targetGroup, currentClinics, leavingGroup, onAddSecond, onMove, onClose,
}) {
  const warnings = getWarnings(player, targetGroup);
  // "Move" only ever vacates the clinic the drag came from. If they're in
  // three, say which one they'd keep rather than implying a clean transfer.
  const staying = currentClinics.filter(g => g.id !== leavingGroup?.id);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3 flex-shrink-0">
          <div>
            <h2 className="font-bold text-gray-800 text-base flex items-center gap-2">
              <span className="text-amber-500" aria-hidden="true">⚠</span>
              {player.name} is already in a clinic
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Nothing has changed yet — choose what this should do.
            </p>
          </div>
          <button onClick={onClose} className="text-xl text-gray-400 hover:text-gray-600 leading-none flex-shrink-0">×</button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Currently in
            </div>
            <ul className="space-y-1 text-sm text-gray-700">
              {currentClinics.map(g => (
                <li key={g.id} className="bg-gray-50 border border-gray-200 rounded px-3 py-1.5">
                  <GroupLine group={g} />
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Being added to
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded px-3 py-1.5 text-sm text-gray-700">
              <GroupLine group={targetGroup} />
              <span className="text-gray-500"> · {targetGroup.playerIds.length}/4</span>
            </div>
          </div>

          {warnings.length > 0 && (
            <ul className="space-y-1">
              {warnings.map((w, i) => (
                <li key={i} className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-1.5">
                  {w}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Replacing is what nearly every one of these turns out to be, so it
            is the button under the thumb. Attending two clinics is rare — it
            stays one click away, as a link rather than a second big button. */}
        <div className="px-5 py-4 border-t border-gray-100 space-y-2 flex-shrink-0">
          <button
            onClick={onMove}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2 rounded-lg transition-colors"
          >
            Replace current clinic
            <span className="block text-xs font-normal opacity-90">
              {leavingGroup ? `Leaves ${leavingGroup.id} for ${targetGroup.id}` : `Joins ${targetGroup.id}`}
              {staying.length > 0 && leavingGroup ? ` · stays in ${staying.map(g => g.id).join(", ")}` : ""}
            </span>
          </button>
          <div className="flex items-center justify-between gap-3 pt-0.5">
            <button
              onClick={onAddSecond}
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors text-left"
              title={`Keeps ${currentClinics.map(g => g.id).join(", ")} and adds ${targetGroup.id}`}
            >
              Add as a second clinic instead
            </button>
            <button
              onClick={onClose}
              className="text-xs text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
